import * as React from 'react';

import { useBoolean } from 'ahooks';
import { Badge, Button, Divider, Form, Modal, Select, Typography } from 'antd';

import { CheckOutlinedIcon } from 'components';
import { usePurchaseAction, usePurchaseValue } from 'context';
import type { PurchaseStatus } from 'types';
import { localizePurchaseStatus } from 'utils';

const purchaseStatuses = [
  'Purchased',
  'Posted',
  'Delivered',
  'Cancelled',
] satisfies PurchaseStatus[];

type FieldType = Pick<ReturnType<typeof usePurchaseValue>, 'status'>;

const PurchaseFormAction = () => {
  const [isCancelledStatus, { set: setCancelledStatus }] = useBoolean();
  const [submitModalInstance, submitModalContextHolder] = Modal.useModal();
  const { defaultStatus, mode } = usePurchaseValue();
  const action = usePurchaseAction();

  const { label: defaultStatusLabel, color: defaultStatusColor } =
    localizePurchaseStatus(defaultStatus);

  return (
    <div>
      <div className='px-6 pt-4'>
        <Typography.Title level={3} className='mb-0 text-base font-medium'>
          انتشار سفارش
        </Typography.Title>
      </div>

      <Divider className='mb-8 mt-4' />

      <div className='px-6'>
        <Typography.Paragraph>
          <Badge
            text={`وضعیت فعلی: ${defaultStatusLabel}`}
            color={defaultStatusColor}
          />
        </Typography.Paragraph>
        <Form.Item<FieldType>
          label='وضعیت جدید'
          help={
            isCancelledStatus ? (
              <Typography.Text type='danger'>
                در صورت تایید لغو سفارش، امکان بازگردانی وجود نخواهد داشت!
              </Typography.Text>
            ) : null
          }
          name='status'
          className='mb-2'
        >
          <Select<PurchaseStatus | undefined>
            allowClear
            placeholder='وضعیت جدید سفارش را انتخاب کنید'
            options={purchaseStatuses.map(status => {
              const { label } = localizePurchaseStatus(status);
              return {
                label,
                value: status,
              };
            })}
            onChange={value => {
              setCancelledStatus(value === 'Cancelled');
            }}
          />
        </Form.Item>
      </div>

      <div className='flex flex-wrap-reverse items-center gap-2 p-6 *:m-0 *:shrink *:grow *:basis-40'>
        <Form.Item>
          <Button
            block
            type='primary'
            htmlType='submit'
            icon={<CheckOutlinedIcon />}
            onClick={async event => {
              // Prevent default submit for now
              event.preventDefault();

              const confirmed = await submitModalInstance.confirm({
                title: 'آیا از اعمال تغییرات این سفارش اطمینان دارید؟',
                cancelText: 'انصراف',
                okText: isCancelledStatus ? 'لغو سفارش' : 'تایید',
                okType: isCancelledStatus ? 'danger' : 'primary',
                width: 500,
              });

              if (confirmed) {
                action({
                  type: 'UPDATE_PURCHASE_CAN_SUBMIT',
                  payload: {
                    canSubmit: true,
                  },
                });
              }
            }}
          >
            {mode === 'Create' ? (
              <span>افزودن سفارش جدید</span>
            ) : (
              <span>ذخیره تغییرات</span>
            )}
          </Button>

          <React.Fragment>{submitModalContextHolder}</React.Fragment>
        </Form.Item>
      </div>
    </div>
  );
};

export { PurchaseFormAction };
